import React from 'react';

import { Accordion, Box, Col, Text } from '@opendoor/bricks-next';
import type { BoxProps } from '@opendoor/bricks-next';
import articleEntryLookup from 'cms/entries/articleV2';
import type { Awaited, EntryComponent, IRenderContext } from 'cms/entries/entries';
import type { IIdToLoaderData } from 'cms/loaders';
import RichTextRenderer from 'components/articles-v2/renderer';
import Container from 'components/landing-pages-v2/shared/Container';
import { getComponentThemeColors } from 'components/landing-pages-v2/shared/styles/ComponentTheme';
import type { IComponentThemeOptions } from 'components/landing-pages-v2/shared/styles/ComponentTheme';
import {
  Desc,
  Subhead,
  SubheadSmall,
  TitleWithAccent,
} from 'components/landing-pages-v2/shared/Typography';
import CardCarouselV2 from 'components/shared/CardCarouselV2';
import { CardImage } from 'components/shared/Multicard/MulticardCard';
import type { IComponentMulticard, IComponentMulticardCard } from 'declarations/contentful';
import 'swiper/css';
import 'swiper/css/pagination';

const columns = {
  Two: 2,
  Three: 3,
  Four: 4,
};

interface IComponentMulticardCardProps {
  card: IComponentMulticardCard;
  componentTheme: IComponentThemeOptions;
  isArched: boolean;
  idToDataLoader: IIdToLoaderData | undefined;
}

type ArchBoxProps = BoxProps & { isArched: boolean };

const ArchBox = React.forwardRef<HTMLDivElement, ArchBoxProps>(({ isArched, ...props }, ref) => (
  <Box
    ref={ref}
    {...props}
    $platform-web={{
      ...props['$platform-web'],
      borderRadius: isArched ? '99em 99em 8em 8em' : '24px',
    }}
  />
));
ArchBox.displayName = 'ArchBox';

const AccordionCardDetails = ({ card, isArched, idToDataLoader }: IComponentMulticardCardProps) => (
  <>
    {card.fields.image?.fields?.file?.url && (
      <CardImage
        isArched={isArched}
        src={card.fields.image?.fields?.file?.url}
        alt={card.fields.image?.fields?.description || card.fields.image?.fields?.title}
      />
    )}
    <Accordion
      variant="regular"
      size="medium"
      isFullWidth
      analyticsName="cosmos-articles-multicard-accordion"
      items={[
        {
          title: card.fields.title || '',
          eyebrowText: card.fields.eyebrow,
          description: (
            <Col gap="$2">
              {card.fields.subhead && (
                <Text tag="p" typography="$bodyMedium" fontWeight="$medium" color="$contentPrimary">
                  {card.fields.subhead}
                </Text>
              )}
              {card.fields.description ? (
                <RichTextRenderer.Component
                  body={card.fields.description}
                  idToLoaderData={idToDataLoader as IIdToLoaderData}
                  entryLookup={articleEntryLookup}
                />
              ) : (
                <Box display="block" />
              )}
            </Col>
          ),
        },
      ]}
    />
  </>
);

const CardSectionHeader = ({ entry }: { entry: IComponentMulticard }) => {
  const { fields } = entry;
  return (
    <Col gap="$2">
      <Col>
        {fields.title && (
          <TitleWithAccent
            title={fields.title}
            titleAccent={fields.titleAccent}
            titleSize={fields.titleSize === 'Large' ? ['64px', null, '88px'] : ['64px']}
          />
        )}
        {(fields.subhead || fields.desc) && (
          <Col gap="$4" maxWidth="$96">
            {fields.subhead &&
              (fields.titleSize === 'Large' ? (
                <Subhead>{fields.subhead}</Subhead>
              ) : (
                <SubheadSmall>{fields.subhead}</SubheadSmall>
              ))}
            {fields.desc && <Desc $largerThanLG={{ fontSize: 24 }}>{fields.desc}</Desc>}
          </Col>
        )}
      </Col>
    </Col>
  );
};

const RenderMulticard = (
  entry: IComponentMulticard,
  _: Awaited<ReturnType<any>>,
  context?: IRenderContext,
) => {
  const { fields } = entry;
  const idToDataLoader = context?.idToLoaderData;
  const columnNumber = fields.columns ? columns[fields.columns] : 3;
  const componentTheme = fields.cardBackgroundColor
    ? getComponentThemeColors(fields.cardBackgroundColor)
    : getComponentThemeColors('White 0');
  const isArched = fields.cardStyle === 'Arch';

  return (
    <Box
      className="embedded-entry-wrapper"
      overflow="hidden"
      $platform-web={{ gridColumn: '1 / -1' }}
    >
      <Col gap="$6">
        {fields.title && (
          <Container>
            <CardSectionHeader entry={entry} />
          </Container>
        )}
        <CardCarouselV2
          id={entry.sys.id}
          cards={fields.cards}
          componentTheme={componentTheme}
          columnNumber={columnNumber}
          isArched={isArched}
          renderCard={(card) => (
            <ArchBox
              pt={card.fields.image?.fields?.file?.url ? '$2' : '$22'}
              pb={card.fields.image?.fields?.file?.url ? '$2' : '$6'}
              px="$6"
              height="min-content"
              isArched={isArched}
              backgroundColor="$backgroundTertiary"
              maxWidth="90vw"
            >
              <AccordionCardDetails
                card={card}
                componentTheme={componentTheme}
                isArched={isArched}
                idToDataLoader={idToDataLoader}
              />
            </ArchBox>
          )}
        />
      </Col>
    </Box>
  );
};

// https://app.contentful.com/spaces/bjlp9d7o6h1o/content_types/componentMulticard/fields
const Multicard: EntryComponent<IComponentMulticard> = {
  render: RenderMulticard,
};

export default Multicard;
